import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api } from '../lib/api';
import { STATUS_META, type DriverRow, type OrderStatus, type ZoneRow } from '../lib/types';
import { OrderDetail } from '../components/OrderDetail';

interface Sample {
  id: string;
  orderNo: string;
  status: OrderStatus;
  receiverName: string;
  receiverAddress: string;
  failedAttempts: number;
  subStatus: string | null;
  createdAt: string;
  expectedDeliveryAt: string | null;
  driver: { id: string; name: string } | null;
}

interface Bucket {
  kind: string;
  label: string;
  action: string;
  severity: 'HIGH' | 'MEDIUM';
  count: number;
  samples: Sample[];
}

/**
 * 异常处理台。
 *
 * 调度员早上打开的第一屏 —— 一次看清「今天有什么不对」,
 * 而不是切六个筛选条件自己找。
 *
 * ★ 每一类都写明**下一步该做什么**。只报「有 3 单异常」而不说该干嘛的告警,
 *   几天之后就会被当成背景噪音直接忽略 —— 那还不如不报。
 */
export function Exceptions({
  drivers,
  zones,
  permissions,
}: {
  drivers: DriverRow[];
  zones: ZoneRow[];
  permissions: string[];
}) {
  const [buckets, setBuckets] = useState<Bucket[] | null>(null);
  /* 店里的时区 —— 详情里「今天/明天」按它算(和订单那一屏同一个来源) */
  const [tenantTimezone, setTenantTimezone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  );
  useEffect(() => {
    void api<{ timezone: string }>('/orders/overview')
      .then((r) => r.timezone && setTenantTimezone(r.timezone))
      .catch(() => undefined);
  }, []);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api<{ buckets: Bucket[]; total: number }>('/orders/exceptions');
      setBuckets(r.buckets);
      setTotal(r.total);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    // 调度台会一直开着,定期刷新 —— 但不要太频繁,异常不是秒级变化的
    const t = setInterval(() => void load(), 60_000);
    return () => clearInterval(t);
  }, [load]);

  // 有问题的排前面,同级按数量 —— 空的分类沉到最后但仍然显示
  const sorted = [...(buckets ?? [])].sort((a, b) => {
    if ((a.count > 0) !== (b.count > 0)) return a.count > 0 ? -1 : 1;
    if (a.severity !== b.severity) return a.severity === 'HIGH' ? -1 : 1;
    return b.count - a.count;
  });

  return (
    <div className="p-4 lg:p-6">
      <div className="mb-4 flex items-baseline gap-3">
        <h1 className="text-xl font-semibold lg:text-2xl text-slate-900 dark:text-slate-100">异常处理</h1>
        {buckets && (
          <span className={total > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600'}>
            {total > 0 ? `${total} 单需要处理` : '一切正常'}
          </span>
        )}
        <button
          onClick={() => void load()}
          disabled={loading}
          className="ml-auto h-9 px-3 rounded-lg border border-slate-300 dark:border-slate-600
                     text-sm text-slate-600 dark:text-slate-400 disabled:opacity-50"
        >
          {loading ? '刷新中…' : '刷新'}
        </button>
      </div>

      {error && (
        <div className="mb-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                        dark:border-red-900 px-4 py-2 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {!buckets && !error && <p className="text-slate-400">加载中…</p>}

      <div className="space-y-4">
        {sorted.map((b) => (
          <BucketCard key={b.kind} bucket={b} onOpen={setDetailId} />
        ))}
      </div>

      {detailId && (
        <OrderDetail
          timezone={tenantTimezone}
          id={detailId}
          drivers={drivers}
          zones={zones}
          permissions={permissions}
          onClose={() => {
            setDetailId(null);
            // 处理完立刻刷新 —— 否则调度员会怀疑自己那一下有没有生效
            void load();
          }}
        />
      )}
    </div>
  );
}

function BucketCard({ bucket: b, onOpen }: { bucket: Bucket; onOpen: (id: string) => void }) {
  const empty = b.count === 0;
  const high = b.severity === 'HIGH';

  return (
    <section
      className={`rounded-xl border overflow-hidden ${
        empty
          ? 'border-slate-200 dark:border-slate-700 opacity-60'
          : high
            ? 'border-red-300 dark:border-red-900'
            : 'border-amber-300 dark:border-amber-900'
      }`}
    >
      <header
        className={`px-4 py-3 ${
          empty
            ? 'bg-slate-50 dark:bg-slate-800/50'
            : high
              ? 'bg-red-50 dark:bg-red-950/40'
              : 'bg-amber-50 dark:bg-amber-950/30'
        }`}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="font-semibold text-slate-900 dark:text-slate-100">{b.label}</h2>
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-bold tabular-nums ${
              empty
                ? 'bg-slate-200 dark:bg-slate-700 text-slate-500'
                : high
                  ? 'bg-red-600 text-white'
                  : 'bg-amber-500 text-white'
            }`}
          >
            {b.count}
          </span>
        </div>
        {/* ★ 告警必须附带下一步动作,否则就是噪音 */}
        <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-400">
          {empty ? '这一类今天是干净的' : `→ ${b.action}`}
        </p>
      </header>

      {b.samples.length > 0 && (
        <ul className="divide-y divide-slate-100 dark:divide-slate-700/50">
          {b.samples.map((s) => (
            <li key={s.id}>
              <button
                onClick={() => onOpen(s.id)}
                className="w-full text-left px-4 py-2.5 flex items-center gap-3
                           hover:bg-slate-50 dark:hover:bg-slate-700/30"
              >
                <span className="font-mono text-xs text-brand-600 dark:text-brand-500 shrink-0">
                  {s.orderNo}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm text-slate-900 dark:text-slate-100">
                  {s.receiverName}
                  <span className="text-slate-400"> · {s.receiverAddress}</span>
                </span>
                {s.failedAttempts > 0 && (
                  <span className="shrink-0 text-xs text-red-600 dark:text-red-400">
                    失败 {s.failedAttempts} 次
                  </span>
                )}
                {s.driver && (
                  <span className="shrink-0 text-xs text-slate-500">{s.driver.name}</span>
                )}
                <span
                  className={`shrink-0 px-1.5 py-0.5 rounded text-xs ${
                    STATUS_META[s.status]?.cls ?? ''
                  }`}
                >
                  {STATUS_META[s.status]?.label ?? s.status}
                </span>
                <span className="shrink-0 text-xs text-slate-400 tabular-nums">
                  {ageOf(s.createdAt)}
                </span>
              </button>
            </li>
          ))}

          {b.count > b.samples.length && (
            <li className="px-4 py-2 text-xs text-slate-500">
              还有 {b.count - b.samples.length} 单 —— 到订单列表按对应条件筛选查看全部
            </li>
          )}
        </ul>
      )}
    </section>
  );
}

/** 「放了多久」比绝对时间更有用 —— 调度员关心的是积压时长 */
function ageOf(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 60) return `${mins} 分钟`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} 小时`;
  return `${Math.floor(hours / 24)} 天`;
}
